import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import cx from "classnames";
import TimeAgo from "react-timeago";
import LinesEllipsis from "react-lines-ellipsis";

import Slider from "src/components/slider";
import InfiniteScroll from "src/components/infiniteScroll";
import Tooltip from "src/components/tooltip";
import { Banner } from "src/types/common";
import { BannerView } from "src/components/banner";
import { fetchGamificationRedemptionsForUser } from "src/redux/actions/coaching/gamification";

import _ from "lodash";
import styles from "./rewardRequestsSlider.module.scss";

const defaultImage = "/images/collections/background.jpeg";

export interface RewardRequestRow {
  id: string;
  rewardName: string;
  banner: Banner | null;
  rewardDesc?: string;
  points: number;
  status: string;
  createdDate?: string;
}

interface RewardRequestsSliderProps {
  show: boolean;
  onClose: () => void;
  rows: RewardRequestRow[];
  loading?: boolean;
  hasMore?: boolean;
  onLoadMore: () => void;
}

const statusLabel = (status: string) => {
  if (status === "PENDING") return "pending";
  if (status === "GRANTED") return "granted";
  if (status === "REJECTED") return "rejected";
  return status.toLowerCase();
};


export const RewardRequestsSlider: React.FC<RewardRequestsSliderProps> = ({
  show,
  onClose,
  rows,
  loading = false,
  hasMore = false,
  onLoadMore,
}) => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (!show) return;
    dispatch(fetchGamificationRedemptionsForUser({ page: 0, limit: 50 }));
  }, [dispatch, show]);

  return (
    <Slider.Dialog
      show={show}
      onClose={onClose}
      width="500px"
      placement="start"
      className={styles.sliderDialog}
    >
      <Slider.Body className={styles.sliderBody}>
        <div className={styles.header}>
          <div className={styles.headerText}>
            <h2 className={styles.title}>reward requests</h2>
            <p className={styles.subtitle}>Track the status of your requests</p>
          </div>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close"
          >
            <i className="paperflite-close" />
          </button>
        </div>

        {!loading && rows.length === 0 && (
          <p className={styles.helper}>No reward requests yet.</p>
        )}

        <InfiniteScroll
          loading={loading}
          hasMore={hasMore}
          loadMore={onLoadMore}
          loader={() => (
            <>
              {_.times(rows.length === 0 ? 5 : 1, (i) => (
                <PlaceholderRewardRequestRow key={i} />
              ))}
            </>
          )}
        >
          <div className={styles.list} aria-busy={loading}>
            {!loading && rows.map((row) => <RewardRequestItem key={row.id} row={row} />)}
          </div>
        </InfiniteScroll>
      </Slider.Body>
    </Slider.Dialog>
  );
};

const RewardRequestItem: React.FC<{ row: RewardRequestRow }> = ({ row }) => {
  const label = statusLabel(row.status || "PENDING");
  const [showTooltip, setShowTooltip] = React.useState(false);
  return (
    <div key={row.id} className={styles.row}>
      <div className={styles.thumbnail}>
      <BannerView
        banner={row.banner as any}
        defaultBanner={defaultImage}
        overlay={false}
      />
      </div>
      <div className={styles.meta}>
        <Tooltip
          content={row.rewardName}
          placement="top"
          disabled={!showTooltip}
        >
          <div className={styles.rewardNameWrapper}>
            <LinesEllipsis
              className={styles.rewardName}
              text={row.rewardName || ""}
              maxLine="1"
              basedOn="letters"
              ellipsis="..."
              onReflow={({ clamped }) => setShowTooltip(clamped)}
            />
          </div>
        </Tooltip>
        <span className={styles.requested}>
          requested{" "}
          {row.createdDate ? <TimeAgo date={row.createdDate} /> : "recently"}
        </span>
      </div>
      <span
        className={cx(styles.badge, {
          [styles.badgePending]: row.status === "PENDING",
          [styles.badgeGranted]: row.status === "GRANTED",
          [styles.badgeRejected]: row.status === "REJECTED",
        })}
      >
        {label}
      </span>
      <div className={styles.cost}>
        <i className={cx("paperflite-coins", styles.coin)} aria-hidden />
        <span>{row.points}</span>
      </div>
    </div>
  );
};

const PlaceholderRewardRequestRow: React.FC = () => (
  <div className={cx(styles.row, styles.placeholder)} aria-hidden>
    <div className={cx(styles.thumbnail, styles.block)} />
    <div className={styles.meta}>
      <div className={cx(styles.name, styles.block)} />
      <div className={cx(styles.description, styles.block)} />
    </div>
  </div>
);